import React, { useEffect, useMemo, useState } from "react";
import { Modal, Checkbox, Button, InputNumber, Tooltip, message } from "antd";
import { CaretRightOutlined, CaretDownOutlined } from "@ant-design/icons";
import {
  getProjectModelDocuments, setProjectModelSourceFilter, getDocumentTransforms, setDocumentTransforms,
} from "api/projects/modelSources";
import { invalidate as invalidateSourceFilter } from "webgl/js/loader/streaming/sourceFilterRegistry";
import { invalidate as invalidatePlacement } from "webgl/js/loader/streaming/documentTransformRegistry";

// "Model files" - the gear on a project card in the personal area.
//
// One row per source document of the live export (the host model first,
// then each linked file). The checkbox says whether the file loads at
// all; under the name sit the file's placement - a move, a turn and a
// scale - so a link inserted origin-to-origin a hundred feet from where
// it belongs is put right here, once, for every viewer.
//
// Two stores sit behind the one dialog and are saved together:
//   - the source filter (hidden document ids): the streaming viewer skips
//     those files' instances while tiles hydrate, so their fragments are
//     never fetched, and the Project Files tree leaves them out;
//   - the document transforms (per file name): applied at tile ingest.
// An unticked file is written to both (hidden in the filter, visible:false
// in the placement) so it stays hidden whichever store a viewer honours.
// Older exports do not tag instances by file: the choice is still saved
// and takes effect on the next export from the plugin.
const T = { muted: "#6B7280", line: "#E5E7EB", ink: "#111827", navy: "#1F3A5F", bg: "#F9FAFB", warn: "#9A3412", warnBg: "#FFF7ED", warnLine: "#FDBA74" };
const AXES = ["x", "y", "z"];
const zero = () => ({ x: 0, y: 0, z: 0 });
const blankPlacement = () => ({ translation: zero(), rotationDeg: zero(), scale: 1 });
const norm = (v) => String(v || "").trim().toLowerCase();
const matches = (entry, doc) => (entry.docId && doc.id && String(entry.docId) === String(doc.id)) || norm(entry.docName) === norm(doc.name);
const isMoved = (p) => AXES.some((a) => Number(p.translation[a]) || Number(p.rotationDeg[a])) || Number(p.scale) !== 1;
const placementOf = (e) => ({
  translation: { ...zero(), ...(e?.translation || {}) },
  rotationDeg: { ...zero(), ...(e?.rotationDeg || {}) },
  scale: Number(e?.scale) > 0 ? Number(e.scale) : 1,
});

export default function ProjectModelSourcesModal({ projectId, projectTitle, open, onClose, canEdit }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [docs, setDocs] = useState([]);
  const [orphans, setOrphans] = useState([]);
  const [filterable, setFilterable] = useState(false);
  const [attribution, setAttribution] = useState("none");
  const [exportVersion, setExportVersion] = useState("");
  const [units, setUnits] = useState("IMPERIAL");
  const [hidden, setHidden] = useState(new Set());
  const [initialHidden, setInitialHidden] = useState(new Set());
  const [placement, setPlacement] = useState({});
  const [initialPlacement, setInitialPlacement] = useState("{}");
  // Which files show their placement fields. A file that already carries
  // an offset opens unfolded; the rest fold under a triangle.
  const [opened, setOpened] = useState(new Set());

  useEffect(() => {
    if (!open || !projectId) return undefined;
    let alive = true;
    setLoading(true);
    Promise.all([
      getProjectModelDocuments(projectId),
      getDocumentTransforms(projectId).catch(() => ({ entries: [] })),
    ])
      .then(([r, tr]) => {
        if (!alive) return;
        const list = Array.isArray(r?.documents) ? r.documents : [];
        const saved = Array.isArray(tr?.entries) ? tr.entries : [];
        setDocs(list);
        setFilterable(!!r?.filterable);
        setAttribution(r?.attribution || (r?.filterable ? "records" : "none"));
        setExportVersion(r?.exportVersion || "");
        setUnits(tr?.units || "IMPERIAL");
        const h = new Set((r?.hiddenDocumentIds || []).map(String));
        const p = {};
        for (const d of list) {
          const e = saved.find((s) => matches(s, d));
          p[d.id] = placementOf(e);
          if (e && e.visible === false) h.add(String(d.id));
        }
        // Entries naming a file this export no longer has are kept as
        // they are, and said so: a republish under a new name should
        // not lose them.
        setOrphans(saved.filter((s) => !list.some((d) => matches(s, d))));
        setHidden(h);
        setInitialHidden(new Set(h));
        setPlacement(p);
        setInitialPlacement(JSON.stringify(p));
        setOpened(new Set(list.filter((d) => isMoved(p[d.id])).map((d) => d.id)));
      })
      .catch((e) => { if (alive) message.error(e?.err || e?.message || "Could not list the model's files"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, projectId]);

  const hiddenDirty = useMemo(() => {
    if (hidden.size !== initialHidden.size) return true;
    for (const id of hidden) if (!initialHidden.has(id)) return true;
    return false;
  }, [hidden, initialHidden]);
  const placementDirty = useMemo(() => JSON.stringify(placement) !== initialPlacement, [placement, initialPlacement]);
  const dirty = hiddenDirty || placementDirty;

  const visibleCount = docs.filter((d) => !hidden.has(d.id)).length;
  const unitLabel = units === "METRIC" ? "m" : "ft";

  const toggle = (id, checked) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (checked) next.delete(id); else next.add(id);
      return next;
    });
  };
  const setAll = (checked) => setHidden(checked ? new Set() : new Set(docs.map((d) => d.id)));
  const setAxis = (id, field, axis, v) => setPlacement((prev) => ({
    ...prev, [id]: { ...prev[id], [field]: { ...prev[id][field], [axis]: Number(v) || 0 } },
  }));
  const setScale = (id, v) => setPlacement((prev) => ({ ...prev, [id]: { ...prev[id], scale: Number(v) > 0 ? Number(v) : 1 } }));
  const reset = (id) => setPlacement((prev) => ({ ...prev, [id]: blankPlacement() }));
  const fold = (id) => setOpened((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const save = async () => {
    if (visibleCount === 0) { message.warning("At least one file must stay visible"); return; }
    setSaving(true);
    try {
      let hiddenNow = hidden;
      if (hiddenDirty) {
        const r = await setProjectModelSourceFilter(projectId, [...hidden]);
        hiddenNow = new Set((r?.hiddenDocumentIds || []).map(String));
        invalidateSourceFilter(projectId);
      }
      const entries = docs
        .map((d) => ({ d, p: placement[d.id] || blankPlacement(), shown: !hiddenNow.has(String(d.id)) }))
        .filter(({ p, shown }) => !shown || isMoved(p))
        .map(({ d, p, shown }) => ({
          docName: d.name, docId: d.id, translation: p.translation, rotationDeg: p.rotationDeg, scale: p.scale, visible: shown, units,
        }))
        .concat(orphans);
      await setDocumentTransforms(projectId, entries);
      invalidatePlacement(projectId);
      setHidden(hiddenNow);
      setInitialHidden(new Set(hiddenNow));
      setInitialPlacement(JSON.stringify(placement));
      const moved = docs.filter((d) => isMoved(placement[d.id] || blankPlacement())).length;
      const parts = [];
      if (hiddenNow.size) parts.push(`${hiddenNow.size} file${hiddenNow.size > 1 ? "s" : ""} hidden`);
      if (moved) parts.push(`${moved} file${moved > 1 ? "s" : ""} moved`);
      message.success(parts.length ? `Saved - ${parts.join(", ")}. Reopen the model to see it.` : "Saved - every file shows in its exported place");
      onClose && onClose(true);
    } catch (e) {
      message.error(e?.err || e?.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const num = (id, field, axis, step) => (
    <InputNumber size="small" style={{ width: 68 }} step={step} disabled={!canEdit}
      value={placement[id]?.[field]?.[axis] ?? 0} onChange={(v) => setAxis(id, field, axis, v)} />
  );
  const axisGroup = (id, label, field, step) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <span style={{ color: T.muted, minWidth: 54 }}>{label}</span>
      {AXES.map((a) => (
        <span key={a} style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
          <span style={{ color: T.muted }}>{a.toUpperCase()}</span>{num(id, field, a, step)}
        </span>
      ))}
    </span>
  );

  return (
    <Modal
      open={open}
      title={`Model files${projectTitle ? ` - ${projectTitle}` : ""}`}
      onCancel={() => onClose && onClose(false)}
      footer={[
        <Button key="cancel" onClick={() => onClose && onClose(false)}>Close</Button>,
        <Button key="save" type="primary" onClick={save} loading={saving} disabled={!canEdit || !dirty || loading || visibleCount === 0}>
          Save
        </Button>,
      ]}
      destroyOnClose
      width={780}
    >
      <p style={{ margin: "0 0 10px", fontSize: 13, color: T.muted }}>
        Tick the files the model should load: the host model and every linked file the last export carried.
        Unticked files are left out of the Project Files tree and skipped while the 3D model streams.
        Under each file, move, turn or scale it - in the export unit ({units === "METRIC" ? "metres" : "feet"}) and degrees,
        Revit's axes (X east, Y north, Z up), turns and scale about the model's centre. The export itself is never
        changed, and a republish keeps these because they follow the file name. Reopen the model after saving.
      </p>
      {!filterable && docs.length > 0 && (
        <div style={{ background: T.warnBg, border: `1px solid ${T.warnLine}`, color: T.warn, borderRadius: 6, padding: "6px 10px", fontSize: 12.5, marginBottom: 10 }}>
          This export carries no per-file element records, so the viewer still loads everything.
          Your choice is saved and applies from the next export.
        </div>
      )}
      {loading ? (
        <div style={{ padding: 14, fontSize: 12.5, color: T.muted }}>Loading files…</div>
      ) : !docs.length ? (
        <div style={{ padding: 14, fontSize: 12.5, color: T.muted }}>
          No exported model yet, or the export did not list its source files. Publish the model from Revit first.
        </div>
      ) : (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, fontSize: 12 }}>
            <span style={{ color: T.muted }}>
              {visibleCount} of {docs.length} file{docs.length > 1 ? "s" : ""} shown{exportVersion ? ` · export ${exportVersion}` : ""}
              {attribution === "tags" ? " · elements tagged by file" : attribution === "records" ? " · elements matched through the export's records" : ""}
            </span>
            <span>
              <Button size="small" type="link" disabled={!canEdit} onClick={() => setAll(true)}>All</Button>
              <Button size="small" type="link" disabled={!canEdit} onClick={() => setAll(false)}>None</Button>
            </span>
          </div>
          <div style={{ border: `1px solid ${T.line}`, borderRadius: 6, maxHeight: 440, overflow: "auto" }}>
            {docs.map((d) => {
              const p = placement[d.id] || blankPlacement();
              const off = hidden.has(d.id);
              return (
                <div key={d.id} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 12px", borderBottom: `1px solid ${T.line}`, background: off ? T.bg : "#fff" }}>
                  <Checkbox style={{ marginTop: 2 }} checked={!off} disabled={!canEdit} onChange={(e) => toggle(d.id, e.target.checked)} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: T.ink, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {d.name}
                    </div>
                    <div style={{ fontSize: 11.5, color: T.muted }}>
                      {d.role === "host" ? "Host model" : d.role === "link" ? "Linked file" : "File"}
                      {d.elementCount ? ` · ${d.elementCount.toLocaleString()} elements` : ""}
                      <Tooltip title={d.id}><span style={{ marginLeft: 6, fontFamily: "monospace" }}>{String(d.id).slice(0, 10)}</span></Tooltip>
                    </div>
                    <button
                      type="button"
                      data-testid={`placement-toggle-${d.id}`}
                      aria-expanded={opened.has(d.id)}
                      onClick={() => fold(d.id)}
                      style={{ background: "none", border: 0, padding: 0, marginTop: 4, fontSize: 11.5, color: T.navy, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}
                    >
                      {opened.has(d.id) ? <CaretDownOutlined /> : <CaretRightOutlined />}
                      Placement{isMoved(p) ? ` - moved ${AXES.filter((a) => Number(p.translation[a])).map((a) => `${a.toUpperCase()} ${p.translation[a]} ${unitLabel}`).join(", ") || "turned or scaled"}` : ""}
                    </button>
                    {opened.has(d.id) && (
                      <div data-testid={`placement-${d.id}`} style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px 14px", marginTop: 4, fontSize: 11, opacity: off ? 0.55 : 1 }}>
                        {axisGroup(d.id, `Move (${unitLabel})`, "translation", units === "METRIC" ? 0.5 : 1)}
                        {axisGroup(d.id, "Turn (°)", "rotationDeg", 1)}
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                          <span style={{ color: T.muted }}>Scale</span>
                          <InputNumber size="small" style={{ width: 68 }} min={0.0001} step={0.05} disabled={!canEdit}
                            value={p.scale} onChange={(v) => setScale(d.id, v)} />
                        </span>
                        <Tooltip title="Back to the export's own placement">
                          <Button size="small" type="link" style={{ padding: 0, height: "auto", fontSize: 11 }} disabled={!canEdit || !isMoved(p)} onClick={() => reset(d.id)}>Reset</Button>
                        </Tooltip>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {orphans.map((e) => (
              <div key={`orphan|${e.docName}|${e.docId}`} style={{ padding: "8px 12px 8px 38px", borderBottom: `1px solid ${T.line}`, background: T.bg }}>
                <div style={{ fontSize: 13, color: T.muted, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.docName}</div>
                <div style={{ fontSize: 11.5, color: T.muted }}>
                  Not in the current export - its placement is kept for the next one
                  {e.visible === false ? " (hidden)" : ""}
                  {isMoved(placementOf(e)) ? ` (moved ${AXES.map((a) => `${a.toUpperCase()} ${Number(e.translation?.[a]) || 0}`).join(" ")} ${unitLabel})` : ""}
                </div>
              </div>
            ))}
          </div>
          {!canEdit && (
            <div style={{ marginTop: 8, fontSize: 12, color: T.muted }}>Only the project owner, a company admin or a member with project edit rights can change this.</div>
          )}
        </>
      )}
    </Modal>
  );
}
